import { Injectable } from '@angular/core';
import { CssUtils, ParsedSelector, SelectorParseOptions } from '@common/flare-data/util/css-utils';
import { ComputedStyle, ComputedStylesheet } from '@portal-core/project-files/stylesheets/models/computed-stylesheet.model';
import { CssProperty, CssRule, CssStylesheet } from '@portal-core/project-files/stylesheets/models/css-stylesheet.model';
import { CssCodeBlock } from '@portal-core/project-files/stylesheets/models/preview-css-style.model';
import { CssStylesheetService } from '@portal-core/project-files/stylesheets/services/css-stylesheet.service';

@Injectable({
  providedIn: 'root'
})
export class ComputedStylesheetService {

  constructor(
    private cssStylesheetService: CssStylesheetService
  ) {

  }

  /** Collects style names that match the specified tags, grouped by base selector.
   * Each group's `baseName` is the selector with attributes/pseudos stripped (per `options`).
   * `variantNames`: the base itself plus pseudo-class/element and attribute variants (single-compound rules).
   * `descendantNames`: combinator rules (descendant / child / sibling) where this base appears anywhere in the chain — so a
   * rule like `.correctFeedback p { ... }` is attached to both the `.correctFeedback` and `p` groups. */
  public collectMatchingStyles(stylesheet: ComputedStylesheet, tags: string[], validIds: string[], options: SelectorParseOptions): { baseName: string; variantNames: string[]; descendantNames: string[] }[] {
    const groups = new Map<string, { variantNames: string[]; descendantNames: string[] }>();
    Object.keys(stylesheet.styles).forEach(styleName => {
      const selectors = CssUtils.parseSelector(styleName, options);
      selectors.forEach(selector => {
        const style = stylesheet.styles[selector.selector];
        if (style?.isHidden || style?.isResponsiveGridTemplateStyle) {
          return;
        }
        // skip the style if it does not match to the tags or ids or has more than one CSS class
        if (selector.tagName && !tags.some(tagName => CssUtils.matchXmlName(selector, tagName))
          || selector.idName && !validIds.includes(selector.idName)
          || selector.classNames.length > 1
        ) {
          return;
        }
        const baseName = selector.selector;
        let group = groups.get(baseName);
        if (!group) {
          group = { variantNames: [], descendantNames: [] };
          groups.set(baseName, group);
        }
        const target = selectors.length === 1 ? group.variantNames : group.descendantNames;
        if (!target.includes(styleName)) {
          target.push(styleName);
        }
      });
    });
    return Array.from(groups, ([baseName, group]) => ({ baseName, variantNames: group.variantNames, descendantNames: group.descendantNames }));
  }

  /** Collects style names that are applicable to the specified selector.
   * `variantNames`: the selector itself plus its tag/class variants plus pseudo-class/element and attribute variants (single-compound rules).
   * `descendantNames`: combinator rules (descendant / child / sibling) where this selector (or its variants) appears anywhere in the chain. */
  public collectApplicableStyles(stylesheet: ComputedStylesheet, selector: ParsedSelector, options: SelectorParseOptions): { variantNames: string[]; descendantNames: string[] } {
    const variantNames = [], descendantNames = [];
    Object.keys(stylesheet.styles).forEach(styleName => {
      const styleSelectors = CssUtils.parseSelector(styleName, options);
      styleSelectors.forEach(styleSelector => {
        const style = stylesheet.styles[styleSelector.selector];
        if (style?.isHidden || style?.isResponsiveGridTemplateStyle) {
          return;
        }
        if (CssUtils.matchStyle(selector, styleSelector)) {
          const target = styleSelectors.length === 1 ? variantNames : descendantNames;
          if (!target.includes(styleName)) {
            target.push(styleName);
          }
        }
      });
    });
    variantNames.sort();
    descendantNames.sort();
    return { variantNames, descendantNames };
  }

  /** Computes the computed stylesheet for the specified CSS stylesheets and medium. */
  public compute(cssStylesheets: CssStylesheet[], medium: string): ComputedStylesheet {
    // collect rules applied to medium
    const allRules: CssRule[] = [];
    cssStylesheets.forEach(stylesheet => {
      const rules = this.cssStylesheetService.getRules(stylesheet, medium);
      allRules.push(...rules);
    });
    // create styles
    const styles: Dictionary<ComputedStyle> = {};
    allRules.forEach(rule => {
      let style = styles[rule.selector];
      if (!style) {
        style = new ComputedStyle();
        style.name = rule.selector;
        styles[style.name] = style;
      }
      if (rule.comment) {
        style.comment = rule.comment;
      }
      rule.items.forEach(item => {
        if (item instanceof CssProperty)
          this.setProperty(style, item);
      });
    });
    // compute additional properties of styles
    Object.values(styles).forEach(style => {
      style.isHidden = this.isHiddenStyle(style);
      style.isResponsiveGridTemplateStyle = this.isResponsiveGridTemplateStyle(style);
    });
    // create computed stylesheet
    const stylesheet = new ComputedStylesheet();
    stylesheet.medium = medium;
    stylesheet.cssStylesheets = cssStylesheets;
    stylesheet.cssRules = allRules;
    stylesheet.styles = styles;
    return stylesheet;
  }

  /** Converts the computed stylesheet to stylesheet content. Allows to patch each nested style individually before converting. */
  public getStylesheetContent(computed: ComputedStylesheet, patch?: (ComputedStyle) => ComputedStyle): string {
    const rules: string[] = [];

    Object.values(computed.styles).forEach(style => {
      if (patch) {
        style = patch(this.cloneStyle(style));
      }
      const selector = style.name;
      const properties = Object.values(style.properties);
      // skip styles without properties
      if (properties.length) {
        const rule = `${selector} { ${properties.join(';')} }`;
        rules.push(rule);
      }
    });

    return rules.join('\n');
  }

  /** Builds a list of code blocks (one per style) for syntax-highlighted display in the picker popover. */
  public getStyleCode(styles: ComputedStyle[]): CssCodeBlock[] {
    return (styles ?? [])
      .filter(s => !!s)
      .map(style => ({
        selector: style.name,
        comment: style.comment ?? undefined,
        properties: Object.values(style.properties)
          .filter(p => p.name !== 'mc-relevant-properties')
          .map(p => ({ name: p.name, value: p.node?.value, important: p.important }))
          .filter(p => p.name && p.value)
          .sort((a, b) => a.name.localeCompare(b.name))
      }));
  }

  /** Gets the style properties, each on separate line. */
  public getStyleDescription(style: ComputedStyle): string {
    if (!style) {
      return '';
    }

    const properties = this.styleToString(style, true, true, ['mc-relevant-properties']);
    return `${style.name}${style.comment ? `\n${style.comment}` : ''}\n{\n${properties}\n}`;
  }

  private cloneStyle(style: ComputedStyle): ComputedStyle {
    const clone = {
      comment: style.comment,
      isResponsiveGridTemplateStyle: style.isResponsiveGridTemplateStyle,
      isHidden: style.isHidden,
      name: style.name,
      properties: { ...style.properties }
    };

    for (const propName in clone.properties) {
      const prop = clone.properties[propName];
      clone.properties[propName] = prop.clone();
    }

    return clone;
  }

  private setProperty(style: ComputedStyle, property: CssProperty): void {
    const existing = style.properties[property.name];
    if (existing?.important && !property.important) {
      return;
    }
    style.properties[property.name] = property;
  }

  private styleToString(style: ComputedStyle, multiline: boolean, indent: boolean, excludedProps: string[] = []): string {
    if (!style)
      return '';
    const delimiter = multiline ? '\n' : ' ';
    return Object.values(style.properties)
      .filter(p => !excludedProps.includes(p.name))
      .map(p => (indent ? '  ' : '') + this.propertyToString(p))
      .sort()
      .join(delimiter);
  }

  private propertyToString(property: CssProperty): string {
    return `${property.node?.prop}: ${property.node?.value};`;
  }

  private isHiddenStyle(style: ComputedStyle): boolean {
    return style.properties['mc-hidden']?.node?.value === 'hidden';
  }

  private isResponsiveGridTemplateStyle(style: ComputedStyle): boolean {
    const prop = style.properties['mc-grid-row'];
    if (prop?.node?.value === 'true') {
      const stylesheet = this.cssStylesheetService.getStylesheet(prop);
      return !!stylesheet?.isFactory;
    }
    return false;
  }
}
